// Update API URL based on window.location.hostname
const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  settings?: Record<string, any>;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  data?: {
    id: string;
    email: string;
    name?: string;
    token: string;
  };
  error?: string;
}

export class AuthService {
  private static debugMode = true;
  private static sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours
  private static retryAttempts = 3;
  private static retryDelay = 1000;

  private static log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any) {
    if (this.debugMode || level === 'error') {
      console[level](`[Auth Service] ${message}`, data);
    }
  }

  private static validateEmail(email: string): boolean {
    if (!email?.trim()) return false;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  }

  private static validatePassword(password: string): boolean {
    return typeof password === 'string' && password.length >= 6;
  }


  private static async handleResponse(response: Response): Promise<AuthResponse> {
    let data: any;
    
    try {
      data = await response.json();
    } catch (error) {
      this.log('error', 'Failed to parse response', error);
      throw new Error('Invalid server response format');
    }

    if (!response.ok || !data.success) {
      const errorMessage = data.message || 'Authentication failed';
      this.log('error', 'Request failed', { 
        status: response.status, 
        success: data.success,
        message: data.message 
      });
      throw new Error(errorMessage);
    }

    if (!data.data?.token) {
      this.log('error', 'No token in response', data);
      throw new Error('No authentication token received');
    }

    if (!data.data?.id || !data.data?.email) {
      this.log('error', 'Invalid user data in response', data);
      throw new Error('Invalid user data received from server');
    }

    return data;
  }

  private static async retryRequest(
    request: () => Promise<Response>, 
    attempt = 1
  ): Promise<Response> {
    try {
      return await request();
    } catch (error) {
      if (
        attempt < this.retryAttempts && 
        error instanceof Error &&
        (error.message.includes('network') || error.message.includes('timeout'))
      ) {
        this.log('warn', `Retry attempt ${attempt}/${this.retryAttempts}`, { error });
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempt));
        return this.retryRequest(request, attempt + 1);
      }
      throw error;
    }
  }

  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    this.log('debug', 'Attempting login', { email: credentials.email });

    if (!this.validateEmail(credentials.email)) {
      throw new Error('Invalid email format');
    }

    if (!this.validatePassword(credentials.password)) {
      throw new Error('Password must be at least 6 characters long');
    }

    try {
      const response = await this.retryRequest(() => 
        fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          body: JSON.stringify(credentials),
          credentials: 'include' // Important: Include credentials for CORS
        })
      );

      const data = await this.handleResponse(response);
      this.log('info', 'Login successful', { userId: data.data?.id });
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Unable to connect to the authentication server. Please check your internet connection.'
        );
      }
      throw error;
    }
  }

  static async register(user: LoginCredentials & { name?: string }): Promise<AuthResponse> {
    this.log('debug', 'Attempting registration', { email: user.email });

    if (!this.validateEmail(user.email)) {
      throw new Error('Invalid email format');
    }

    if (!this.validatePassword(user.password)) {
      throw new Error('Password must be at least 6 characters long');
    }

    try {
      const response = await this.retryRequest(() =>
        fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            email: user.email,
            password: user.password,
            name: user.name || user.email.split('@')[0]
          })
        })
      );

      const data = await this.handleResponse(response);
      this.log('info', 'Registration successful', { userId: data.data?.id });
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error(
          'Unable to connect to the registration server. Please check your internet connection.'
        );
      }
      throw error;
    }
  }

  static async validateSession(): Promise<boolean> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      this.log('warn', 'No token found for validation');
      return false;
    }

    try {
      // Add cache-busting parameter to prevent cached responses
      const cacheBuster = `_cb=${Date.now()}`;
      
      // Log the token being used (first few characters only for security)
      const tokenPreview = token.substring(0, 10) + '...';
      this.log('debug', `Validating session with token: ${tokenPreview}`);
      
      const response = await fetch(`${API_URL}/auth/validate?${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        credentials: 'include', // Important: Include credentials for CORS
        cache: 'no-store' // Prevent caching
      });

      // Log the response status
      this.log('debug', `Validation response status: ${response.status}`);

      if (!response.ok) {
        this.log('warn', 'Session validation failed', { 
          status: response.status,
          statusText: response.statusText
        });
        
        // Try to get more detailed error information
        try {
          const errorData = await response.json();
          this.log('warn', 'Validation error details', errorData);
        } catch (e) {
          // Ignore JSON parsing errors
        }
        
        return false;
      }

      // Try to parse the response to ensure it's valid
      try {
        const data = await response.json();
        this.log('debug', 'Session validation response', data);
        return data.valid === true;
      } catch (e) {
        this.log('warn', 'Failed to parse validation response', e);
        return false;
      }
    } catch (error) {
      this.log('error', 'Session validation error', error);
      return false;
    }
  }

  static async refreshSession(): Promise<AuthResponse['data'] | null> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      this.log('warn', 'No token found for refresh');
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        credentials: 'include', // Important: Include credentials for CORS
        cache: 'no-store' // Prevent caching
      });

      if (!response.ok) {
        this.log('warn', 'Token refresh failed', {
          status: response.status,
          statusText: response.statusText
        });
        
        // Try to get more detailed error information
        try {
          const errorData = await response.json();
          this.log('warn', 'Refresh error details', errorData);
        } catch (e) {
          // Ignore JSON parsing errors
        }
        
        return null;
      }

      try {
        const data = await response.json();
        
        if (!data.data?.token) {
          this.log('warn', 'No token in refresh response', data);
          return null;
        }
        
        // Update the stored token
        localStorage.setItem('auth_token', data.data.token);
        
        // Update token expiry (24 hours from now)
        const expiryTime = Date.now() + this.sessionTimeout;
        localStorage.setItem('auth_token_expiry', expiryTime.toString());
        
        this.log('info', 'Token refreshed successfully');
        return data.data;
      } catch (e) {
        this.log('error', 'Failed to parse refresh response', e);
        return null;
      }
    } catch (error) {
      this.log('error', 'Session refresh failed', error);
      return null;
    }
  }

  static async updateUserSettings(userId: string, settings: Record<string, any>): Promise<void> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${API_URL}/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ settings }),
        credentials: 'include' // Important: Include credentials for CORS
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update user settings');
      }
    } catch (error) {
      this.log('error', 'Settings update failed', error);
      throw error;
    }
  }

  static clearSession(): void {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_token_expiry');
      this.log('info', 'Session cleared');
    } catch (error) {
      this.log('error', 'Failed to clear session', error);
    }
  }
}